import axios from 'axios'

/**
 * Type-safe API error response structure
 */
interface ApiErrorResponse {
  message?: string | string[]
  error?: string
  statusCode?: number
  // correlation id the backend attaches to failures it could not classify. it is the only
  // handle on the real cause (which never crosses the wire), so it must reach the user.
  errorId?: string
}

/**
 * Extracts a user-friendly error message from various error sources
 * Handles Axios errors, validation errors, and generic errors
 */
export const getErrorMessage = (error: unknown): string => {
  if (!error) {
    return 'An unexpected error occurred. Please try again.'
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    const data = error.response?.data as ApiErrorResponse | undefined

    // Handle network errors
    if (!error.response) {
      return 'Network error. Please check your connection and try again.'
    }

    // Handle specific HTTP status codes with fallback to API message
    switch (status) {
      case 400:
        // Bad Request - often validation errors
        if (data?.message) {
          if (Array.isArray(data.message)) {
            return data.message[0] || 'Invalid input. Please check your information.'
          }
          return String(data.message)
        }
        return 'Invalid input. Please check your information.'

      case 401:
        // Unauthorized — can hit any endpoint (bad credentials, expired token, …), so prefer the
        // server's message and fall back to generic copy rather than always claiming a login failure.
        if (data?.message) {
          return Array.isArray(data.message) ? String(data.message[0]) : String(data.message)
        }
        return 'Your session has expired or your credentials are invalid. Please sign in again.'

      case 409:
        // Conflict - duplicate resource or constraint violation
        if (data?.message) {
          return Array.isArray(data.message) ? data.message[0] : String(data.message)
        }
        return 'A conflict occurred. Please try again.'

      case 422:
        // Unprocessable Entity - validation error
        if (data?.message) {
          if (Array.isArray(data.message)) {
            return data.message.join(', ')
          }
          return String(data.message)
        }
        return 'Please check your information and try again.'

      case 429:
        // Too Many Requests — not login-specific (throttle applies to many endpoints); prefer the
        // server message, else generic copy.
        if (data?.message) {
          return Array.isArray(data.message) ? String(data.message[0]) : String(data.message)
        }
        return 'Too many requests. Please try again in a few minutes.'

      case 500: {
        // Internal Server Error — the body's message is deliberately generic, so keep the
        // friendlier copy, but surface the correlation id: it is the one thing that lets this
        // failure be traced back to its cause in the logs.
        const base = 'Server error. Please try again later.'
        return data?.errorId ? `${base} (ref: ${data.errorId})` : base
      }

      case 502:
        // Bad Gateway — an upstream (T-Bank) fault. the server's message names which one.
        if (data?.message) {
          return Array.isArray(data.message) ? String(data.message[0]) : String(data.message)
        }
        return 'An upstream service returned an error. Please try again later.'

      case 503:
        // Service Unavailable — prefer the server's curated message (circuit open, upstream
        // unreachable, encryption not configured) over generic copy; each is actionable.
        if (data?.message) {
          return Array.isArray(data.message) ? String(data.message[0]) : String(data.message)
        }
        return 'Service temporarily unavailable. Please try again later.'

      default:
        // Check if there's a custom message from the API
        if (data?.message) {
          if (Array.isArray(data.message)) {
            return data.message[0] || 'An error occurred. Please try again.'
          }
          return String(data.message)
        }
        if (data?.error) {
          return String(data.error)
        }
        return error.message || 'An error occurred. Please try again.'
    }
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'An unexpected error occurred. Please try again.'
}

/**
 * Checks if an error is a specific HTTP status code
 */
export const isErrorStatus = (error: unknown, status: number): boolean => {
  return axios.isAxiosError(error) && error.response?.status === status
}

/**
 * Checks if an error is a validation error (400 or 422)
 */
export const isValidationError = (error: unknown): boolean => {
  return isErrorStatus(error, 400) || isErrorStatus(error, 422)
}

/**
 * Checks if an error is an authentication error (401)
 */
export const isAuthenticationError = (error: unknown): boolean => {
  return isErrorStatus(error, 401)
}

/**
 * Checks if an error is a conflict error (409 - usually duplicate)
 */
export const isConflictError = (error: unknown): boolean => {
  return isErrorStatus(error, 409)
}
